import { Constraints, ConstraintType } from '@superfunnel/types';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ConstraintsForm from '../components/Constraints/ConstraintsForm';

interface Props {
  initialConstraints: Constraints;
  updatableConstraints?: ConstraintType[];
  open: boolean;
  onClose: () => void;
  onSave: (constraints: Constraints) => Promise<void>;
  title?: string;
  subtitle?: string;
}

/**
 * Modal for editing variant constraints in the AI Assistant draft state.
 * Unlike EditVariantConstraintsModal, this doesn't require DynamicPageContext
 * and saves via a callback instead of calling a service directly.
 */
export default function EditVariantConstraintsDraftModal({
  initialConstraints,
  updatableConstraints,
  open,
  onClose,
  onSave,
  title,
  subtitle,
}: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [constraints, setConstraints] = useState<Constraints>({});

  useEffect(() => {
    setConstraints(initialConstraints || {});
  }, [initialConstraints]);

  useEffect(() => {
    if (open) return;

    setSaving(false);
    setError(undefined);
  }, [open]);

  const handleSave = useCallback(async () => {
    if (saving) return;

    // Validate schedules if they're being edited
    if (!updatableConstraints || updatableConstraints.includes('schedules')) {
      const invalidSchedule = constraints?.schedules?.find((s) => {
        return (
          !s.startDate &&
          !s.endDate &&
          !s.daysOfWeek?.length &&
          !s.hoursOfDay?.length
        );
      });

      if (invalidSchedule) {
        setError(
          'Schedules must have at least one of: startDate, endDate, daysOfWeek, hoursOfDay',
        );
        return;
      }
    }

    setSaving(true);
    setError(undefined);

    try {
      await onSave(constraints);
    } catch (e: unknown) {
      console.error('[EditVariantConstraintsDraftModal] Error saving:', e);
      setError(e instanceof Error ? e.message : 'Failed to save constraints');
      setSaving(false);
    }
  }, [saving, constraints, updatableConstraints, onSave]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: 'Update',
        type: 'primary',
        loading: saving,
        onClick: () => handleSave(),
      },
    ],
    [saving, onClose, handleSave],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      width="lg"
      title={title || 'Smart Traffic Rules Configuration'}
    >
      <p className="mt-0 text-sm text-gray-500">
        {subtitle ||
          `Define and manage the rules for directing traffic to this variant. Use
        smart constraints like devices, keywords, ad groups, ads and audience
        preferences to ensure your variant is shown to the right users.`}
      </p>
      <div className="mt-4 flex flex-col gap-4">
        <ConstraintsForm
          visibleConstraints={updatableConstraints}
          constraints={constraints}
          onChange={(newConstraints) => {
            setConstraints(newConstraints);
            setError(undefined);
          }}
        />
      </div>
      {error && <div className="mt-5 w-full text-sm text-red-600">{error}</div>}
    </Modal>
  );
}
